import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, CheckCircle2, AlertTriangle, ArrowRight, Zap, RotateCcw, Plane, Landmark, Coffee, ShieldCheck, Luggage, Heart, TrendingUp, Navigation, Route, ShoppingBag } from "lucide-react";
import { Shell } from "../components/Shell";
import { api, eur } from "../lib/api";
import { tripDateContext } from "../lib/goal";
import { loadSelectedTrip } from "../lib/selectedTrip";
import { useI18n } from "../i18n";
import { toast } from "sonner";

export default function Journey() {
  const { t, lang } = useI18n();
  const [j, setJ] = useState(null);
  const [dis, setDis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected] = useState(() => loadSelectedTrip());
  const selectedDateContext = tripDateContext(selected, lang);

  useEffect(() => { api.journey().then(setJ).catch(() => {}); setDis(null); }, [lang]);

  const simulate = async () => {
    setLoading(true);
    try { const r = await api.simulateDisruption(); setDis(r); toast.warning(t("journey.demoTriggered"), { description: t("journey.demoTriggeredDesc") }); }
    catch { toast.error(t("journey.simErr")); } finally { setLoading(false); }
  };

  if (selected) {
    return (
      <Shell active="journey">
        <div className="mb-5">
          <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.24em] text-slate-400 font-display"><span style={{ color: "var(--brand)" }}>{t("journey.kicker")}</span></div>
          <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("journey.title")}</h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="selected-journey-route">
            <span className="dv-pill px-3 py-1.5 text-sm" style={{ background: "#fff", border: "1px solid var(--line)" }}><MapPin size={13} color="var(--brand)" /> {selected.origin || "Paris"}</span>
            <ArrowRight size={15} className="text-slate-300" />
            <span className="dv-pill px-3 py-1.5 text-sm" style={{ background: "#fff", border: "1px solid var(--line)" }}><MapPin size={13} color="var(--brand)" /> {selected.destination}</span>
          </div>
        </div>

        <div className="dv-card overflow-hidden mb-5" data-testid="selected-journey">
          {selected.image && <img src={selected.image} alt={selected.destination} className="w-full h-52 object-cover" />}
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display">{t("journey.selectedTrip")}</div>
                <div className="mt-1 font-display font-extrabold text-2xl sm:text-3xl">{selected.destination}</div>
                {(selected.region || selected.country) && <div className="mt-1 text-sm text-slate-500">{[selected.region, selected.country].filter(Boolean).join(" · ")}</div>}
                {selectedDateContext && (
                  <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid="selected-journey-dates">
                    <Clock size={14} color="var(--brand)" /> {selectedDateContext.label}
                  </div>
                )}
              </div>
              {selected.total != null && (
                <div className="text-right">
                  <div className="text-[10px] font-semibold tracking-[0.2em] text-slate-400 font-display">{t("common.total")}</div>
                  <div className="dv-total text-3xl text-slate-900">{eur(selected.total)}</div>
                </div>
              )}
            </div>
            <div className="mt-5 rounded-2xl p-4" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>
              <div className="text-sm font-semibold text-slate-800">{t("journey.itineraryPendingTitle")}</div>
              <p className="mt-1 text-sm text-slate-500 leading-relaxed">{t("journey.itineraryPendingBody")}</p>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  if (!j) return <Shell active="journey"><div className="dv-skeleton h-[400px] rounded-2xl" /></Shell>;

  return (
    <Shell active="journey">
      <div className="mb-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.24em] text-slate-400 font-display"><span style={{ color: "var(--brand)" }}>{t("journey.kicker")}</span></div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("journey.title")}</h1>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {j.route.map((r, i) => (
            <React.Fragment key={r}>
              <span className="dv-pill px-3 py-1.5 text-sm" style={{ background: "#fff", border: "1px solid var(--line)" }}><MapPin size={13} color="var(--brand)" /> {r}</span>
              {i < j.route.length - 1 && <ArrowRight size={15} className="text-slate-300" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {!dis ? (
          <motion.div key="normal" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} className="dv-card p-5 sm:p-6 mb-5" data-testid="next-action-card" style={{ borderColor: "var(--book-soft)" }}>
            <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] font-display" style={{ color: "var(--book-ink)" }}>{t("journey.yourNextStep")}</div>
            <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-display font-extrabold text-2xl sm:text-3xl">{j.next_action.title}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Stat label={t("journey.leaveHomeBy")} value={j.next_action.leave_home_by} />
                  <Stat label={t("journey.bagDropCloses")} value={j.next_action.bag_drop_closes} />
                  <Stat label={t("journey.flight")} value={j.flight_status} tone="book" />
                </div>
              </div>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "var(--book-soft)" }}><Luggage size={22} color="var(--book-ink)" /></div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="risk" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden dv-card mb-5" data-testid="disruption-card" style={{ background: "var(--alert-soft)", borderColor: "var(--alert)" }}>
            <div className="p-5 sm:p-7">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 dv-pill text-[11px] px-3 py-1.5" style={{ background: "var(--alert)", color: "#fff" }}><AlertTriangle size={13} /> {dis.banner_title}</div>
                <span className="dv-pill text-[10px] px-2.5 py-1" style={{ background: "#fff", color: "var(--alert-ink)", border: "1px solid var(--alert)" }}>{t("journey.demoDisruption")}</span>
              </div>
              <div className="mt-4 font-display font-extrabold text-2xl sm:text-3xl" style={{ color: "var(--alert-ink)" }}>{dis.detail}</div>
              <div className="mt-4 grid sm:grid-cols-3 gap-2.5">
                <RiskStat label={t("journey.originalConnection")} value={dis.original_connection} />
                <RiskStat label={t("journey.connectionMargin")} value={`${dis.connection_margin_min} min`} danger />
                <RiskStat label={t("journey.status")} value={t("journey.atRisk")} danger />
              </div>
              <div className="mt-5 rounded-2xl p-4 bg-white flex items-start gap-3" style={{ border: "1px solid var(--alert)" }}>
                <Zap size={18} color="var(--alert)" className="shrink-0 mt-0.5" />
                <div>
                  <div className="text-[11px] font-semibold" style={{ color: "var(--alert-ink)" }}>{t("journey.recommendedAction")}</div>
                  <div className="font-display font-bold text-lg mt-0.5">{dis.recommended_action}</div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{dis.explanation}</p>
                </div>
              </div>
              <button onClick={() => setDis(null)} className="dv-btn dv-btn-ghost text-sm px-4 py-2.5 mt-4"><RotateCcw size={14} /> {t("journey.resetJourney")}</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="dv-card p-5 sm:p-6 mb-5">
        <div className="font-display font-bold text-lg mb-4">{t("journey.timelineTitle")}</div>
        <div className="relative pl-2">
          {j.steps.map((s, i) => {
            const risky = dis && (s.id === "connection");
            const isNext = !dis && s.state === "next";
            return (
              <div key={s.id} className="relative flex gap-4 pb-5 last:pb-0" data-testid={`timeline-${s.id}`}>
                {i < j.steps.length - 1 && <span className="absolute left-[11px] top-6 bottom-0 w-px" style={{ background: risky ? "var(--alert)" : "var(--line-strong)" }} />}
                <span className="relative z-10 w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: risky ? "var(--alert)" : isNext ? "var(--book)" : "#fff", border: `2px solid ${risky ? "var(--alert)" : isNext ? "var(--book)" : "var(--line-strong)"}` }}>
                  {risky ? <AlertTriangle size={12} color="#fff" /> : isNext ? <CheckCircle2 size={13} color="#fff" /> : <span className="w-2 h-2 rounded-full" style={{ background: "var(--line-strong)" }} />}
                </span>
                <div className="flex-1 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: risky ? "var(--alert-ink)" : "var(--ink)" }}>{s.label}</div>
                    {isNext && <div className="text-[11px]" style={{ color: "var(--book-ink)" }}>{t("journey.nextStepSmall")}</div>}
                    {risky && <div className="text-[11px]" style={{ color: "var(--alert-ink)" }}>{t("journey.nowRisky", { m: dis.connection_margin_min })}</div>}
                  </div>
                  <span className="font-mono-num text-sm text-slate-500 flex items-center gap-1"><Clock size={12} /> {s.time}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!dis && (
        <button onClick={simulate} disabled={loading} data-testid="simulate-disruption-button" className="dv-btn w-full text-base py-4 mb-10" style={{ background: "var(--alert-soft)", color: "var(--alert-ink)", border: "1.5px solid var(--alert)" }}>
          <AlertTriangle size={17} /> {loading ? t("journey.simulating") : t("journey.simulateDisruption")}
        </button>
      )}

      {/* Beyond the traveller — scale value (secondary, below journey) */}
      <section className="mt-2 mb-6">
        <div className="mb-5">
          <div className="flex items-center gap-2">
            <span className="w-8 h-px" style={{ background: "var(--line-strong)" }} />
            <span className="text-[11px] font-semibold tracking-[0.24em] text-slate-400 font-display">{t("journey.beyondKicker")}</span>
          </div>
          <h3 className="mt-2 font-display font-bold text-xl sm:text-2xl tracking-tight text-slate-800">{t("journey.beyondTitle")}</h3>
          <p className="mt-1 text-sm text-slate-500 max-w-xl">{t("journey.beyondSub")}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="dv-card p-5 sm:p-6" data-testid="airlines-card">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><Plane size={18} color="var(--brand)" /></div>
              <span className="font-display font-bold text-lg">{t("journey.forAirlines")}</span>
            </div>
            <div className="rounded-xl px-3.5 py-2.5" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>
              <div className="text-[10px] font-semibold tracking-widest text-slate-400 font-display mb-0.5">{t("journey.airlineBefore").toUpperCase()}</div>
              <div className="text-sm text-slate-500">{t("journey.airlineBeforeQuote")}</div>
            </div>
            <div className="flex justify-center py-1.5"><ArrowRight size={16} className="rotate-90 text-slate-300" /></div>
            <div className="rounded-xl px-3.5 py-2.5" style={{ background: "var(--brand-soft)", border: "1px solid rgba(37,99,235,0.2)" }}>
              <div className="text-[10px] font-semibold tracking-widest font-display mb-0.5" style={{ color: "var(--brand-ink)" }}>{t("journey.airlineWith").toUpperCase()}</div>
              <div className="text-sm font-medium" style={{ color: "var(--brand-ink)" }}>{t("journey.airlineWithQuote")}</div>
            </div>
            <div className="mt-4 space-y-2.5">
              {[[Zap, t("journey.airlineBenefit1")], [Heart, t("journey.airlineBenefit2")], [TrendingUp, t("journey.airlineBenefit3")]].map(([Ic, txt], i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}><Ic size={14} color="var(--brand)" /></span>
                  {txt}
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs font-semibold rounded-lg px-3 py-2" style={{ background: "var(--brand-soft)", color: "var(--brand-ink)" }}>{t("journey.airlineCore")}</div>
          </div>

          <div className="dv-card p-5 sm:p-6" data-testid="airports-card">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "var(--wait-soft)" }}><Landmark size={18} color="var(--wait-ink)" /></div>
              <span className="font-display font-bold text-lg">{t("journey.forAirports")}</span>
            </div>
            <div className="rounded-xl px-3.5 py-3 flex items-start gap-2.5" style={{ background: "var(--wait-soft)", border: "1px solid rgba(217,119,6,0.18)" }}>
              <Coffee size={16} color="var(--wait-ink)" className="mt-0.5 shrink-0" />
              <span className="text-sm font-medium" style={{ color: "var(--wait-ink)" }}>{t("journey.airportExample")}</span>
            </div>
            <div className="mt-4 space-y-2.5">
              {[[Navigation, t("journey.airportItem1")], [Route, t("journey.airportItem2")], [ShoppingBag, t("journey.airportItem3")]].map(([Ic, txt], i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}><Ic size={14} color="var(--wait-ink)" /></span>
                  {txt}
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs font-semibold rounded-lg px-3 py-2" style={{ background: "var(--wait-soft)", color: "var(--wait-ink)" }}>{t("journey.airportCore")}</div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
          <ShieldCheck size={13} color="var(--book)" /> {t("journey.beyondDemoNote")}
        </div>
      </section>
    </Shell>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="dv-pill px-3 py-2" style={{ background: tone === "book" ? "var(--book-soft)" : "var(--paper)", border: "1px solid var(--line)" }}>
    <span className="text-[11px] text-slate-400">{label}</span>
    <span className="font-mono-num text-sm font-semibold" style={{ color: tone === "book" ? "var(--book-ink)" : "var(--ink)" }}>{value}</span>
  </div>
);
const RiskStat = ({ label, value, danger }) => (
  <div className="rounded-xl px-3 py-2.5 bg-white" style={{ border: `1px solid ${danger ? "var(--alert)" : "var(--line)"}` }}>
    <div className="text-[11px] text-slate-400">{label}</div>
    <div className="font-mono-num text-base font-bold" style={{ color: danger ? "var(--alert)" : "var(--ink)" }}>{value}</div>
  </div>
);
